import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation, fadeInUp, staggerContainer } from '../../hooks/useScrollAnimation';
import SectionTitle from '../ui/SectionTitle';
import { useLang } from '../context/LanguageContext';
import { galleryItems } from '../../data/gallery';
import { pick } from '../../utils/i18n';

const GallerySection = ({ id }) => {
  const { ref, inView } = useScrollAnimation();
  const { t, lang } = useLang();
  const [activeCategory, setActiveCategory] = useState(0);
  const categories = t.gallery.categories;

  const filteredItems =
    activeCategory === 0 ? galleryItems : galleryItems.filter((item) => item.category === activeCategory);

  return (
    <section id={id} className="section-padding bg-gradient-to-b from-[#F5F9E3] via-white to-white" ref={ref}>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container-max"
      >
        <SectionTitle badge={t.gallery.badge} title={t.gallery.title} />

        {/* Category filters */}
        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat, i) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(i)}
              aria-pressed={activeCategory === i}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === i
                  ? 'bg-primary text-white'
                  : 'bg-[#EAF5EE] text-secondary hover:bg-[#DCEFE2]'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Mosaico: cada foto conserva su proporción original */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="columns-1 sm:columns-2 lg:columns-3 gap-4"
          >
            {filteredItems.map((item) => (
              <div key={item.src} className="group relative mb-4 rounded-xl overflow-hidden break-inside-avoid">
                <img
                  src={item.src}
                  alt={pick(item.alt, lang)}
                  width={item.w}
                  height={item.h}
                  loading="lazy"
                  className="w-full h-auto block group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute left-4 right-4 bottom-3 flex flex-col items-start [text-shadow:0_1px_2px_rgba(0,0,0,.5)]">
                  <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {categories[item.category]}
                  </span>
                  {item.credit && (
                    <span className="text-[11px] text-white/90">
                      {t.gallery.photoCredit}: {pick(item.credit, lang)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

export default GallerySection;
